import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faSearch } from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../../base-components/CustomModal";
import Popup from "../../../base-components/Popup";
import TablePaginationActions from "../../../base-components/TablePaginationActions";
import { getSessionUser, isDynamicStaffRole } from "../../../utils/roleAccess";
import { PageHeader } from "../../../ui";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const selectClass =
  "border border-gx-navy/20 bg-gx-canvas w-full min-h-[44px] px-3 py-2.5 pr-10 rounded-gx-control outline-0 text-sm text-gx-ink appearance-none bg-[length:12px_8px] bg-[right_0.85rem_center] bg-no-repeat bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22 viewBox=%220 0 12 8%22 fill=%22none%22%3E%3Cpath d=%22M1 1.5L6 6.5L11 1.5%22 stroke=%22%235B676F%22 stroke-width=%221.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/%3E%3C/svg%3E')] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red";
const labelClass = "block text-sm font-medium text-gx-ink mb-1.5";
const modalHeaderClass = "px-6 pr-14 pb-4 pt-5 border-b border-gx-border";
const modalBodyClass = "px-6 py-5 text-gx-ink";
const modalFooterClass = "flex justify-end gap-3 pt-5 mt-6 border-t border-gx-border";

function SuperAdminUsers() {
  const sessionUser = useMemo(() => getSessionUser(), []);
  const actingUserId = sessionUser?._id;

  const [query, setQuery] = useState("");
  const [companyOption, setCompanyOption] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [roleCompany, setRoleCompany] = useState(null);
  const [roleCompanyOptions, setRoleCompanyOptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [saving, setSaving] = useState(false);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const showPopup = (success, message) => {
    setIsSuccessAlert(success);
    setPopupMessage(message);
    setIsPopupOpen(true);
    setTimeout(() => setIsPopupOpen(false), 4000);
  };

  useEffect(() => {
    if (!actingUserId) return;
    fetch(`${BASE_URL}super-admin/roles?actingUserId=${actingUserId}`)
      .then((r) => r.json())
      .then((data) => setRoles(data.roles || []))
      .catch((err) => console.error(err));
  }, [actingUserId]);

  const searchCompanies = useCallback(
    async (q, setter, setLoading) => {
      if (!actingUserId || !q || q.trim().length < 1) {
        setter([]);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}super-admin/companies/search?q=${encodeURIComponent(q)}&actingUserId=${actingUserId}`
        );
        const data = await res.json();
        setter(data.companies || []);
      } catch (err) {
        console.error(err);
        setter([]);
      } finally {
        setLoading(false);
      }
    },
    [actingUserId]
  );

  const searchUsers = async () => {
    if (!actingUserId) return;
    if (!query.trim() && !companyOption) {
      setUsers([]);
      return;
    }
    setSearching(true);
    try {
      const params = new URLSearchParams({ actingUserId });
      if (query.trim()) params.set("q", query.trim());
      if (companyOption?._id) params.set("company_id", companyOption._id);
      const res = await fetch(`${BASE_URL}super-admin/users/search?${params}`);
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || data.code || "Search failed");
        setUsers([]);
      } else {
        setUsers(data.users || []);
        setPage(0);
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const loadLocationsForCompany = async (companyId) => {
    if (!companyId) {
      setLocations([]);
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}locations/getLocationsByCompanyID/${companyId}`);
      const data = await res.json();
      setLocations(data.locations || []);
    } catch (err) {
      console.error(err);
      setLocations([]);
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role || "");
    const company =
      user.company_id && typeof user.company_id === "object"
        ? user.company_id
        : null;
    setRoleCompany(company);
    setRoleCompanyOptions(company ? [company] : []);
    setSelectedLocationId(
      user.location_id?._id || user.location_id || ""
    );
    if (company?._id) {
      loadLocationsForCompany(company._id);
    } else {
      setLocations([]);
    }
    setIsRoleModalOpen(true);
  };

  const needsLocationScope = (roleName) => {
    if (!roleName) return false;
    if (roleName === "ClubAdmin") return true;
    const roleDoc = roles.find((r) => r.name === roleName);
    if (roleDoc?.is_dynamic) return true;
    return isDynamicStaffRole(roleName, roleDoc);
  };

  const handleSaveRole = async () => {
    if (!actingUserId || !selectedUser || !selectedRole) return;
    setSaving(true);
    try {
      const body = {
        actingUserId,
        role: selectedRole,
      };

      // Only send company/location when explicitly choosing them in the modal
      if (roleCompany?._id) {
        body.company_id = roleCompany._id;
      }
      if (needsLocationScope(selectedRole)) {
        body.location_id = selectedLocationId || null;
        if (roleCompany?._id) {
          body.company_id = roleCompany._id;
        }
      }
      if (selectedRole === "SuperAdmin") {
        body.company_id = null;
        body.location_id = null;
      }
      if (selectedRole === "Admin" && roleCompany?._id) {
        body.company_id = roleCompany._id;
      }

      const res = await fetch(
        `${BASE_URL}super-admin/users/${selectedUser._id}/role`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to update role");
      } else {
        showPopup(true, "User role updated successfully");
        setIsRoleModalOpen(false);
        searchUsers();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  const paginated =
    rowsPerPage > 0
      ? users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : users;

  if (!sessionUser || String(sessionUser.role).toLowerCase() !== "superadmin") {
    return (
      <div className="m-5 bg-gx-border p-5 rounded-lg">
        <h3 className="text-xl font-bold text-center text-gx-ink-soft">
          Super Admin access required
        </h3>
      </div>
    );
  }

  return (
    <>
      <div className="m-5"><PageHeader title="User Management" /></div>
      <div className="m-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm text-gx-ink-soft">Name or Email</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
            placeholder="Search by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchUsers()}
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="text-sm text-gx-ink-soft">Company</label>
          <Autocomplete
            options={companyOptions}
            loading={companyLoading}
            value={companyOption}
            onChange={(_, value) => setCompanyOption(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") {
                searchCompanies(value, setCompanyOptions, setCompanyLoading);
              }
            }}
            getOptionLabel={(opt) =>
              opt?.company_name
                ? `${opt.company_name}${opt.company_email ? ` (${opt.company_email})` : ""}`
                : ""
            }
            isOptionEqualToValue={(a, b) => a?._id === b?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search companies"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {companyLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>
        <button
          className="gx-cta flex items-center gap-2"
          onClick={searchUsers}
          disabled={searching}
        >
          <FontAwesomeIcon icon={faSearch} />
          {searching ? "Searching..." : "Search"}
        </button>
      </div>

      {users.length > 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((user) => (
                <TableRow hover key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    {user.company_id?.company_name || "—"}
                  </TableCell>
                  <TableCell>{user.location_id?.name || "—"}</TableCell>
                  <TableCell>
                    <div
                      className="text-gx-ink font-bold hover:cursor-pointer hover:text-gx-red"
                      onClick={() => openRoleModal(user)}
                    >
                      <FontAwesomeIcon icon={faPen} /> Change Role
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  colSpan={6}
                  count={users.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-5 rounded-lg m-5">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">
            {searching ? "Searching..." : "Search for users by name, email, or company"}
          </h3>
        </div>
      )}

      <CustomModal
        isModal={isRoleModalOpen}
        handleModal={() => setIsRoleModalOpen(false)}
        size="md"
        titleId="super-admin-change-role-title"
      >
        <div className={modalHeaderClass}>
          <h1 id="super-admin-change-role-title" className="font-semibold text-2xl text-gx-ink tracking-tight text-balance">
            Change Role
          </h1>
          <p className="text-sm text-gx-ink-soft mt-1.5 leading-relaxed">
            Update platform access for this user.
          </p>
        </div>
        <div className={modalBodyClass}>
          <div className="mb-6 pb-4 border-b border-gx-border/80">
            <p className="font-semibold text-gx-ink truncate">{selectedUser?.name}</p>
            <p className="text-sm text-gx-ink-soft truncate mt-0.5">{selectedUser?.email}</p>
          </div>

          <div className="flex flex-col gap-6">
            <div>
              <label className={labelClass} htmlFor="super-admin-user-role">
                Role
              </label>
              <select
                id="super-admin-user-role"
                className={selectClass}
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">Select role</option>
                {roles.map((r) => (
                  <option key={r._id} value={r.name}>
                    {r.name}
                    {r.is_system ? " (system)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {selectedRole !== "SuperAdmin" && (
              <div>
                <label className={labelClass} htmlFor="super-admin-user-company">
                  Company
                </label>
                <Autocomplete
                  id="super-admin-user-company"
                  options={roleCompanyOptions}
                  value={roleCompany}
                  onChange={(_, value) => {
                    setRoleCompany(value);
                    setSelectedLocationId("");
                    if (value?._id) loadLocationsForCompany(value._id);
                    else setLocations([]);
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      searchCompanies(value, setRoleCompanyOptions, () => {});
                    }
                  }}
                  getOptionLabel={(opt) =>
                    opt?.company_name
                      ? `${opt.company_name}${opt.company_email ? ` (${opt.company_email})` : ""}`
                      : ""
                  }
                  isOptionEqualToValue={(a, b) => a?._id === b?._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select company"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: 44,
                          borderRadius: "10px",
                          backgroundColor: "var(--gx-canvas, #F4F6F8)",
                          "& fieldset": { borderColor: "var(--gx-border, #E2E8ED)" },
                          "&:hover fieldset": { borderColor: "var(--gx-border, #E2E8ED)" },
                          "&.Mui-focused fieldset": { borderColor: "var(--gx-red, #D71920)" },
                        },
                      }}
                    />
                  )}
                />
              </div>
            )}

            {needsLocationScope(selectedRole) && (
              <div>
                <label className={labelClass} htmlFor="super-admin-user-location">
                  Location <span className="text-gx-red" aria-hidden="true">*</span>
                </label>
                <select
                  id="super-admin-user-location"
                  className={selectClass}
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  required
                >
                  <option value="">Select location</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs leading-relaxed text-gx-ink-soft">
                  Required for ClubAdmin and custom location-scoped roles.
                </p>
              </div>
            )}
          </div>

          <div className={modalFooterClass}>
            <button
              type="button"
              className="gx-cta-secondary min-h-[44px] px-5"
              onClick={() => setIsRoleModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gx-cta min-h-[44px] px-5"
              onClick={handleSaveRole}
              disabled={
                saving ||
                !selectedRole ||
                (needsLocationScope(selectedRole) && !selectedLocationId)
              }
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </CustomModal>

      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? "success" : "failure"}
          message={popupMessage}
        />
      )}
    </>
  );
}

export default SuperAdminUsers;
